import { useIsAuthor } from '@/features/auth/useIsAuthor'
import { useGetCatalogQuery, useGetExploreQuery } from '@/features/catalog/catalogApi'
import { useCatalogParams } from '@/features/catalog/hooks/useCatalogParams'
import {
  hasActiveCatalogFilters,
  hasRemovableFilterChips,
} from '@/features/catalog/lib/catalogFilters'
import { CatalogFilterBar } from '@/features/catalog/components/CatalogFilterBar'
import { CatalogResults } from '@/features/catalog/components/CatalogResults'
import { useLanguage } from '@/hooks/useLanguage'
import { cn } from '@/lib/utils'
import { CARD_PAGE_PX_CLASS } from '@/components/catalog/cardGrid'
import { PAGE_MIN_H_CLASS } from '@/components/layout/pageHeight'

const LIMIT = 24

const Catalog = () => {
  const { page, search, categoriesFilter, difficultyFilter, languageFilter, sort, update, reset } =
    useCatalogParams()
  const { language } = useLanguage()
  // Лента Explore — только авторам (не-автору api отвечает 403); остальным — каталог, как
  // до ленты. Пока статус автора неизвестен, не запрашивается ни то, ни другое.
  const { isAuthor, isLoading: authorLoading } = useIsAuthor()

  const query = {
    page,
    limit: LIMIT,
    sort,
    search: search || undefined,
    categoriesFilter: categoriesFilter.length ? categoriesFilter : undefined,
    difficultyFilter: difficultyFilter.length ? difficultyFilter : undefined,
    languageFilter,
  }
  const explore = useGetExploreQuery(query, { skip: authorLoading || !isAuthor })
  const catalog = useGetCatalogQuery(query, { skip: authorLoading || isAuthor })
  const feed = isAuthor ? explore : catalog
  const { data, isFetching, isError, refetch } = feed
  const isLoading = authorLoading || feed.isLoading

  // TODO: Remove client-side filter when backend supports ?type=gamified.
  const items = (data?.items ?? []).filter((c) => c.type === 'gamified')
  const totalPages = data?.pages ?? 0
  const total = data?.total ?? items.length
  const filterState = { search, categoriesFilter, difficultyFilter, languageFilter }
  const hasActiveFilters = hasActiveCatalogFilters(filterState, language)
  const hasRemovableChips = hasRemovableFilterChips(filterState, language)

  return (
    <div className={cn(PAGE_MIN_H_CLASS, 'relative overflow-hidden')}>
      {/* Отступы — как у «Моих курсов»: поиск выше, без пустой полосы сверху. */}
      <div
        className={cn('relative z-10 py-4 transition-all duration-200 md:py-6', CARD_PAGE_PX_CLASS)}
      >
        <div className="h-2" />

        <CatalogFilterBar
          search={search}
          categoriesFilter={categoriesFilter}
          languageFilter={languageFilter}
          onSearchSubmit={(s) => update({ search: s })}
          onCategoriesChange={(ids) => update({ categoriesFilter: ids })}
          onLanguageFilterChange={(v) => update({ languageFilter: v })}
        />

        <CatalogResults
          items={items}
          total={total}
          page={page}
          totalPages={totalPages}
          isLoading={isLoading}
          isFetching={isFetching}
          isError={isError}
          hasActiveFilters={hasActiveFilters}
          hasRemovableChips={hasRemovableChips}
          sort={sort}
          onSortChange={(s) => update({ sort: s })}
          onRetry={refetch}
          onReset={reset}
          onShowAllLanguages={() => update({ languageFilter: 'all' })}
          onPageChange={(p) => update({ page: p })}
        />
      </div>
    </div>
  )
}

export default Catalog
